/**
 * ATELIER VERNIS — Shopify Online Store 2.0 Storefront Script
 * Handles:
 * 1. Top Navigator & Live Shade Search
 * 2. Left Sidebar Collection Navigator
 * 3. Left Sidebar Color Swatch Filter (under Collections)
 * 4. Product Sorting & Active Filter Pills
 * 5. Slide-out Shopping Bag Drawer + Shopify AJAX Cart API
 */

(function () {
  'use strict';

  const catalogSection = document.querySelector('[data-catalog-section]');
  const initialCollection =
    (catalogSection && catalogSection.dataset.initialCollection) || 'all';

  const state = {
    activeCollection: initialCollection,
    selectedColors: new Set(),
    searchQuery: '',
    inStockOnly: false,
    sortBy: 'best-selling',
    cartItems: [],
  };

  // Elements
  const productGrid = document.querySelector('[data-product-grid]');
  const productCards = Array.from(document.querySelectorAll('[data-product-card]'));
  const collectionLinks = Array.from(document.querySelectorAll('[data-collection-filter]'));
  const topNavLinks = Array.from(document.querySelectorAll('[data-top-nav-collection]'));
  const colorButtons = Array.from(document.querySelectorAll('[data-color-filter]'));
  const clearColorsBtn = document.querySelector('[data-clear-colors]');
  const resetAllBtns = Array.from(document.querySelectorAll('[data-reset-all-filters]'));
  const searchInput = document.querySelector('[data-shade-search]');
  const inStockCheckbox = document.querySelector('[data-in-stock-only]');
  const sortSelect = document.querySelector('[data-sort-select]');
  const visibleCountEl = document.querySelector('[data-visible-count]');
  const emptyStateEl = document.querySelector('[data-catalog-empty]');
  const activeTitleEl = document.querySelector('[data-active-collection-title]');
  const activeDescEl = document.querySelector('[data-active-collection-desc]');
  const activeFiltersBar = document.querySelector('[data-active-filters-bar]');
  const activeFilterPills = document.querySelector('[data-active-filter-pills]');
  const sidebarEl = document.querySelector('[data-catalog-sidebar]');
  const sidebarToggleBtn = document.querySelector('[data-sidebar-toggle]');

  if (sortSelect && sortSelect.value) {
    state.sortBy = sortSelect.value;
  }
  if (inStockCheckbox) {
    state.inStockOnly = inStockCheckbox.checked;
  }

  function cardMatchesCollection(card, collectionHandle) {
    if (!collectionHandle || collectionHandle === 'all') return true;
    const handles = (card.dataset.collections || 'all')
      .split(',')
      .map((s) => s.trim().toLowerCase());
    return handles.includes(collectionHandle.toLowerCase());
  }

  function updateColorCountsForCollection() {
    colorButtons.forEach((btn) => {
      const color = (btn.dataset.colorFilter || '').toLowerCase();
      const countInCollection = productCards.filter(
        (card) =>
          cardMatchesCollection(card, state.activeCollection) &&
          (card.dataset.color || '').toLowerCase() === color
      ).length;

      let badge = btn.querySelector('.color-filter-swatch__count');
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'color-filter-swatch__count';
        btn.appendChild(badge);
      }
      badge.textContent = countInCollection;
    });
  }

  function renderActiveFilterPills() {
    if (!activeFiltersBar || !activeFilterPills) return;

    activeFilterPills.innerHTML = '';
    const hasColors = state.selectedColors.size > 0;
    const hasSearch = state.searchQuery.trim().length > 0;

    if (!hasColors && !hasSearch) {
      activeFiltersBar.hidden = true;
      if (clearColorsBtn) clearColorsBtn.hidden = true;
      return;
    }

    activeFiltersBar.hidden = false;
    if (clearColorsBtn) clearColorsBtn.hidden = !hasColors;

    state.selectedColors.forEach((colorKey) => {
      const btn = colorButtons.find((b) => b.dataset.colorFilter === colorKey);
      const label = btn ? btn.dataset.colorLabel || colorKey : colorKey;
      const pill = document.createElement('button');
      pill.type = 'button';
      pill.className = 'filter-pill';
      pill.innerHTML = `<span>Color: ${label}</span><span class="filter-pill__remove">&times;</span>`;
      pill.addEventListener('click', () => {
        state.selectedColors.delete(colorKey);
        syncFilterUI();
      });
      activeFilterPills.appendChild(pill);
    });

    if (hasSearch) {
      const pill = document.createElement('button');
      pill.type = 'button';
      pill.className = 'filter-pill';
      pill.innerHTML = `<span>Search: "${state.searchQuery}"</span><span class="filter-pill__remove">&times;</span>`;
      pill.addEventListener('click', () => {
        state.searchQuery = '';
        if (searchInput) searchInput.value = '';
        syncFilterUI();
      });
      activeFilterPills.appendChild(pill);
    }
  }

  function sortCards() {
    if (!productGrid || productCards.length === 0) return;

    const sorted = [...productCards].sort((a, b) => {
      const priceA = parseFloat(a.dataset.price || '0');
      const priceB = parseFloat(b.dataset.price || '0');
      const titleA = a.dataset.title || '';
      const titleB = b.dataset.title || '';
      const indexA = parseInt(a.dataset.featuredIndex || '0', 10);
      const indexB = parseInt(b.dataset.featuredIndex || '0', 10);
      const bestA = parseInt(a.dataset.bestSellingIndex || a.dataset.featuredIndex || '0', 10);
      const bestB = parseInt(b.dataset.bestSellingIndex || b.dataset.featuredIndex || '0', 10);

      if (state.sortBy === 'best-selling') return bestA - bestB;
      if (state.sortBy === 'price-asc') return priceA - priceB;
      if (state.sortBy === 'price-desc') return priceB - priceA;
      if (state.sortBy === 'title-asc') return titleA.localeCompare(titleB);
      return indexA - indexB;
    });

    sorted.forEach((card) => productGrid.appendChild(card));
  }

  // On live Shopify store, fetch Shopify's native best-selling rank for the active collection
  function fetchShopifyBestSellingOrder(collectionHandle) {
    if (typeof window === 'undefined' || !window.Shopify) return;
    const handle = collectionHandle || 'all';
    fetch(`/collections/${encodeURIComponent(handle)}?sort_by=best-selling`)
      .then((res) => (res.ok ? res.text() : ''))
      .then((html) => {
        if (!html) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const rankedCards = Array.from(doc.querySelectorAll('[data-product-card]'));
        if (rankedCards.length === 0) return;
        const rankById = new Map();
        rankedCards.forEach((c, idx) => {
          if (c.dataset.productId) rankById.set(c.dataset.productId, idx + 1);
        });
        productCards.forEach((card) => {
          const rank = rankById.get(card.dataset.productId);
          if (rank) card.dataset.bestSellingIndex = String(rank);
        });
        if (state.sortBy === 'best-selling') {
          sortCards();
        }
      })
      .catch(() => {});
  }

  function syncFilterUI() {
    // Sync Left Sidebar Collection links
    collectionLinks.forEach((link) => {
      const isMatch = link.dataset.collectionFilter === state.activeCollection;
      link.classList.toggle('is-active', isMatch);
      if (isMatch && activeTitleEl) {
        activeTitleEl.textContent = link.dataset.collectionTitle || link.textContent.trim();
        if (activeDescEl) {
          const desc = (link.dataset.collectionDesc || '').trim();
          activeDescEl.textContent = desc;
          activeDescEl.hidden = !desc;
        }
      }
    });

    // Sync Top Navigator links
    topNavLinks.forEach((link) => {
      const isMatch = link.dataset.topNavCollection === state.activeCollection;
      link.classList.toggle('is-active', isMatch);
    });

    // Sync Color Swatch buttons
    colorButtons.forEach((btn) => {
      const key = btn.dataset.colorFilter;
      const active = state.selectedColors.has(key);
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    updateColorCountsForCollection();
    renderActiveFilterPills();
    sortCards();

    // Filter product cards
    let visibleCount = 0;
    const query = state.searchQuery.trim().toLowerCase();

    productCards.forEach((card) => {
      const inCollection = cardMatchesCollection(card, state.activeCollection);
      const cardColor = (card.dataset.color || '').toLowerCase();
      const inColor =
        state.selectedColors.size === 0 || state.selectedColors.has(cardColor);
      const inSearch =
        !query ||
        (card.dataset.title || '').includes(query) ||
        cardColor.includes(query);
      const inStock =
        !state.inStockOnly || card.dataset.available !== 'false';

      const isVisible = inCollection && inColor && inSearch && inStock;
      card.hidden = !isVisible;
      if (isVisible) visibleCount += 1;
    });

    if (visibleCountEl) {
      visibleCountEl.textContent = visibleCount;
    }

    if (emptyStateEl) {
      emptyStateEl.hidden = visibleCount > 0;
    }
  }

  // Event Listeners: Left Sidebar Collections Navigator
  collectionLinks.forEach((link) => {
    link.addEventListener('click', (e) => {
      const handle = link.dataset.collectionFilter;
      if (handle && productCards.length > 0) {
        e.preventDefault();
        state.activeCollection = handle;
        fetchShopifyBestSellingOrder(handle);
        syncFilterUI();
      }
    });
  });

  // Event Listeners: Top Navigator Links
  topNavLinks.forEach((link) => {
    link.addEventListener('click', (e) => {
      const handle = link.dataset.topNavCollection;
      if (handle && productCards.length > 0) {
        e.preventDefault();
        state.activeCollection = handle;
        fetchShopifyBestSellingOrder(handle);
        syncFilterUI();
      }
    });
  });

  // Event Listeners: Left Sidebar Color Filter Swatches
  colorButtons.forEach((btn) => {
    btn.addEventListener('click', () => {
      const colorKey = btn.dataset.colorFilter;
      if (!colorKey) return;
      if (state.selectedColors.has(colorKey)) {
        state.selectedColors.delete(colorKey);
      } else {
        state.selectedColors.add(colorKey);
      }
      syncFilterUI();
    });
  });

  if (clearColorsBtn) {
    clearColorsBtn.addEventListener('click', () => {
      state.selectedColors.clear();
      syncFilterUI();
    });
  }

  resetAllBtns.forEach((btn) => {
    btn.addEventListener('click', () => {
      state.selectedColors.clear();
      state.searchQuery = '';
      state.inStockOnly = false;
      if (searchInput) searchInput.value = '';
      if (inStockCheckbox) inStockCheckbox.checked = false;
      syncFilterUI();
    });
  });

  if (searchInput) {
    searchInput.addEventListener('input', (e) => {
      state.searchQuery = e.target.value || '';
      syncFilterUI();
    });
  }

  if (inStockCheckbox) {
    inStockCheckbox.addEventListener('change', (e) => {
      state.inStockOnly = Boolean(e.target.checked);
      syncFilterUI();
    });
  }

  if (sortSelect) {
    sortSelect.addEventListener('change', (e) => {
      state.sortBy = e.target.value || 'best-selling';
      syncFilterUI();
    });
  }

  fetchShopifyBestSellingOrder(state.activeCollection);

  const sidebarToggleBtns = Array.from(document.querySelectorAll('[data-sidebar-toggle]'));

  function toggleLeftNavigator() {
    if (!catalogSection) return;
    const isCollapsed = catalogSection.classList.toggle('is-sidebar-collapsed');
    const isExpanded = !isCollapsed;
    if (sidebarEl) {
      sidebarEl.classList.toggle('is-open', isExpanded);
    }
    sidebarToggleBtns.forEach((btn) => {
      btn.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
    });
  }

  sidebarToggleBtns.forEach((btn) => {
    btn.addEventListener('click', toggleLeftNavigator);
  });

  /* ==========================================================================
     SLIDE-OUT CART DRAWER
     ========================================================================== */
  const cartDrawer = document.querySelector('[data-cart-drawer]');
  const cartBackdrop = document.querySelector('[data-cart-backdrop]');
  const cartOpenBtns = Array.from(document.querySelectorAll('[data-cart-drawer-open]'));
  const cartCloseBtns = Array.from(document.querySelectorAll('[data-cart-drawer-close]'));
  const cartItemsContainer = document.querySelector('[data-cart-items]');
  const cartCountBadges = Array.from(document.querySelectorAll('[data-cart-count]'));
  const cartDrawerCount = document.querySelector('[data-cart-drawer-count]');
  const cartSubtotalEl = document.querySelector('[data-cart-subtotal]');
  const shippingMsgEl = document.querySelector('[data-shipping-message]');
  const shippingFillEl = document.querySelector('[data-shipping-fill]');

  function openCartDrawer() {
    if (!cartDrawer) return;
    cartDrawer.classList.add('is-open');
    cartDrawer.setAttribute('aria-hidden', 'false');
    if (cartBackdrop) cartBackdrop.hidden = false;
  }

  function closeCartDrawer() {
    if (!cartDrawer) return;
    cartDrawer.classList.remove('is-open');
    cartDrawer.setAttribute('aria-hidden', 'true');
    if (cartBackdrop) cartBackdrop.hidden = true;
  }

  cartOpenBtns.forEach((btn) => btn.addEventListener('click', openCartDrawer));
  cartCloseBtns.forEach((btn) => btn.addEventListener('click', closeCartDrawer));
  if (cartBackdrop) cartBackdrop.addEventListener('click', closeCartDrawer);

  function renderCartDrawer() {
    const totalCount = state.cartItems.reduce((sum, item) => sum + item.qty, 0);
    const totalCents = state.cartItems.reduce((sum, item) => sum + item.priceCents * item.qty, 0);
    const thresholdCents = parseInt(
      (cartDrawer && cartDrawer.dataset.freeShippingThreshold) || '4500',
      10
    );

    cartCountBadges.forEach((el) => (el.textContent = totalCount));
    if (cartDrawerCount) cartDrawerCount.textContent = `(${totalCount})`;
    if (cartSubtotalEl) cartSubtotalEl.textContent = `$${(totalCents / 100).toFixed(2)}`;

    if (shippingMsgEl && shippingFillEl) {
      const remaining = Math.max(0, thresholdCents - totalCents);
      const pct = Math.min(100, Math.round((totalCents / thresholdCents) * 100));
      shippingFillEl.style.width = `${pct}%`;
      shippingMsgEl.textContent =
        remaining === 0
          ? 'You have unlocked complimentary shipping!'
          : `Add $${(remaining / 100).toFixed(2)} more for complimentary shipping`;
    }

    if (!cartItemsContainer) return;

    if (state.cartItems.length === 0) {
      cartItemsContainer.innerHTML = `<p class="cart-drawer__empty">Your bag is currently empty.</p>`;
      return;
    }

    cartItemsContainer.innerHTML = state.cartItems
      .map(
        (item) => `
        <div class="cart-item">
          <span class="cart-item__swatch" style="background-color: ${item.hex};"></span>
          <div class="cart-item__info">
            <p class="cart-item__title">${item.title}</p>
            <p class="cart-item__meta">${item.finish} &middot; Qty: ${item.qty}</p>
          </div>
          <p class="cart-item__price">$${((item.priceCents * item.qty) / 100).toFixed(2)}</p>
        </div>
      `
      )
      .join('');
  }

  function renderShopifyCartData(cartData) {
    if (!cartData) return;
    cartCountBadges.forEach((el) => (el.textContent = cartData.item_count));
    if (cartDrawerCount) cartDrawerCount.textContent = `(${cartData.item_count})`;
    if (cartSubtotalEl) cartSubtotalEl.textContent = `$${(cartData.total_price / 100).toFixed(2)}`;
    if (!cartItemsContainer) return;
    if (!cartData.items || cartData.items.length === 0) {
      cartItemsContainer.innerHTML = `<p class="cart-drawer__empty">Your bag is currently empty.</p>`;
      return;
    }
    cartItemsContainer.innerHTML = cartData.items
      .map(
        (item) => `
        <div class="cart-item" data-cart-item-key="${item.key}">
          ${
            item.image
              ? `<a href="${item.url}" class="cart-item__thumb-link"><img src="${item.image}" alt="${item.product_title}" class="cart-item__thumb" width="56" height="56"></a>`
              : ''
          }
          <div class="cart-item__info">
            <p class="cart-item__title"><a href="${item.url}">${item.product_title}</a></p>
            <p class="cart-item__meta">$${(item.final_price / 100).toFixed(2)}</p>
            <div class="cart-item__controls">
              <button type="button" class="cart-item__qty-btn" data-cart-change-key="${item.key}" data-cart-change-qty="${item.quantity - 1}" aria-label="Decrease quantity">&minus;</button>
              <span class="cart-item__qty-val">${item.quantity}</span>
              <button type="button" class="cart-item__qty-btn" data-cart-change-key="${item.key}" data-cart-change-qty="${item.quantity + 1}" aria-label="Increase quantity">&#43;</button>
              <button type="button" class="cart-item__remove-btn" data-cart-change-key="${item.key}" data-cart-change-qty="0" aria-label="Remove item">Remove</button>
            </div>
          </div>
          <p class="cart-item__price">$${(item.final_line_price / 100).toFixed(2)}</p>
        </div>
      `
      )
      .join('');
  }

  function syncShopifyCartDrawer() {
    if (typeof window === 'undefined' || !window.Shopify) return;
    fetch((window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/') + 'cart.js')
      .then((res) => (res.ok ? res.json() : null))
      .then(renderShopifyCartData)
      .catch(() => {});
  }

  document.addEventListener('click', (e) => {
    const changeBtn = e.target.closest('[data-cart-change-key]');
    if (changeBtn && window.Shopify) {
      const key = changeBtn.dataset.cartChangeKey;
      const qty = Math.max(0, parseInt(changeBtn.dataset.cartChangeQty || '0', 10));
      const rootUrl = (window.Shopify.routes && window.Shopify.routes.root) || '/';
      fetch(rootUrl + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: qty }),
      })
        .then((res) => (res.ok ? res.json() : null))
        .then(renderShopifyCartData)
        .catch(() => {});
      return;
    }

    const addBtn = e.target.closest('[data-add-to-cart]');
    if (!addBtn) return;

    const id = addBtn.dataset.variantId || addBtn.dataset.productTitle;
    const title = addBtn.dataset.productTitle || 'Nail Polish';
    const rawCents = parseInt(addBtn.dataset.productRawPrice || '1800', 10);
    const hex = addBtn.dataset.productHex || '#D8B8A3';
    const finish = addBtn.dataset.productFinish || 'Nail Lacquer';

    // Attempt Shopify AJAX cart API if running on live Shopify storefront
    if (window.Shopify && !String(id).startsWith('demo-')) {
      const rootUrl = (window.Shopify.routes && window.Shopify.routes.root) || '/';
      fetch(rootUrl + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] }),
      })
        .then(() => {
          syncShopifyCartDrawer();
          openCartDrawer();
        })
        .catch(() => {
          window.location.href = '/cart';
        });
      return;
    }

    const existing = state.cartItems.find((item) => item.id === id);
    if (existing) {
      existing.qty += 1;
    } else {
      state.cartItems.push({ id, title, priceCents: rawCents, hex, finish, qty: 1 });
    }

    renderCartDrawer();
    openCartDrawer();
  });

  /* ==========================================================================
     HOMEPAGE ANNOUNCEMENT SLIDESHOW
     ========================================================================== */
  const slideshowEl = document.querySelector('[data-slideshow]');
  if (slideshowEl && slideshowEl.dataset.slideshowBound !== 'true') {
    slideshowEl.dataset.slideshowBound = 'true';
    const track = slideshowEl.querySelector('[data-slideshow-track]');
    const slides = Array.from(slideshowEl.querySelectorAll('[data-slide]'));
    const dots = Array.from(slideshowEl.querySelectorAll('[data-slide-dot]'));
    const prevBtn = slideshowEl.querySelector('[data-slide-prev]');
    const nextBtn = slideshowEl.querySelector('[data-slide-next]');
    const autoplay = slideshowEl.dataset.autoplay !== 'false';
    const speed = parseInt(slideshowEl.dataset.speed || '5000', 10);
    let currentSlide = 0;
    let timer = null;

    function goToSlide(index) {
      if (slides.length === 0) return;
      currentSlide = (index + slides.length) % slides.length;
      if (track) {
        track.style.transform = `translate3d(-${currentSlide * 100}%, 0, 0)`;
      }
      slides.forEach((slide, idx) => {
        slide.classList.toggle('is-active', idx === currentSlide);
      });
      dots.forEach((dot, idx) => {
        const active = idx === currentSlide;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', active ? 'true' : 'false');
      });
    }

    function startAutoplay() {
      if (!autoplay || slides.length <= 1) return;
      stopAutoplay();
      timer = setInterval(() => {
        goToSlide(currentSlide + 1);
      }, speed);
    }

    function stopAutoplay() {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    }

    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const idx = parseInt(dot.dataset.slideDot || '0', 10);
        goToSlide(idx);
        startAutoplay();
      });
    });

    if (prevBtn) {
      prevBtn.addEventListener('click', () => {
        goToSlide(currentSlide - 1);
        startAutoplay();
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        goToSlide(currentSlide + 1);
        startAutoplay();
      });
    }

    slideshowEl.addEventListener('mouseenter', stopAutoplay);
    slideshowEl.addEventListener('mouseleave', startAutoplay);

    // Support Shopify Theme Editor block selection
    document.addEventListener('shopify:block:select', (e) => {
      const idx = slides.indexOf(e.target);
      if (idx !== -1) {
        stopAutoplay();
        goToSlide(idx);
      }
    });

    startAutoplay();
  }

  // Hide any inactive (0-product) collections in the left navigator when enabled
  const onlyActiveCols =
    !catalogSection || catalogSection.dataset.onlyActiveCollections !== 'false';
  if (onlyActiveCols && productCards.length > 0) {
    collectionLinks.forEach((link) => {
      const handle = link.dataset.collectionFilter;
      if (!handle || handle === 'all') return;
      const count = productCards.filter((card) =>
        cardMatchesCollection(card, handle)
      ).length;
      const countEl = link.querySelector('.collection-nav-list__count');
      if (countEl) countEl.textContent = count;
      const listItem = link.closest('.collection-nav-list__item');
      if (listItem) {
        listItem.hidden = count === 0;
      }
    });
  }

  /* ==========================================================================
     PRODUCT DETAIL GALLERY & QUANTITY CONTROLS
     ========================================================================== */
  const productGallery = document.querySelector('[data-product-gallery]');
  if (productGallery) {
    const slides = Array.from(productGallery.querySelectorAll('[data-gallery-slide]'));
    const thumbs = Array.from(productGallery.querySelectorAll('[data-gallery-thumb]'));
    const prevBtn = productGallery.querySelector('[data-gallery-prev]');
    const nextBtn = productGallery.querySelector('[data-gallery-next]');
    const counterEl = productGallery.querySelector('[data-gallery-counter]');
    let activeIdx = Math.max(
      0,
      slides.findIndex((s) => s.classList.contains('is-active'))
    );

    function setGallerySlide(index) {
      if (slides.length === 0) return;
      activeIdx = (index + slides.length) % slides.length;
      slides.forEach((slide, idx) => {
        slide.classList.toggle('is-active', idx === activeIdx);
      });
      thumbs.forEach((thumb, idx) => {
        thumb.classList.toggle('is-active', idx === activeIdx);
      });
      if (counterEl) {
        counterEl.textContent = `${activeIdx + 1} / ${slides.length}`;
      }
    }

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const idx = parseInt(thumb.dataset.galleryThumb || '0', 10);
        setGallerySlide(idx);
      });
    });

    if (prevBtn) {
      prevBtn.addEventListener('click', () => setGallerySlide(activeIdx - 1));
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', () => setGallerySlide(activeIdx + 1));
    }
  }

  const qtyControl = document.querySelector('[data-qty-control]');
  if (qtyControl) {
    const qtyInput = qtyControl.querySelector('[data-qty-input]');
    const minusBtn = qtyControl.querySelector('[data-qty-minus]');
    const plusBtn = qtyControl.querySelector('[data-qty-plus]');

    if (minusBtn && qtyInput) {
      minusBtn.addEventListener('click', () => {
        const current = parseInt(qtyInput.value || '1', 10);
        qtyInput.value = String(Math.max(1, current - 1));
      });
    }
    if (plusBtn && qtyInput) {
      plusBtn.addEventListener('click', () => {
        const current = parseInt(qtyInput.value || '1', 10);
        qtyInput.value = String(current + 1);
      });
    }
  }

  // Initial UI sync
  syncFilterUI();
})();

